import { uploadFilesDirect } from 'util/api'

import { ASSIGNED_DIFFICULTIES } from '@all-for-one/shared-types'
import { FeedbackOverlay } from 'components/lesson-builder/components/feedback-overlay'

import UploadBox from 'components/upload-box'
import editIcon from 'img/edit.svg'
import imageIcon from 'img/image.svg'
import refreshBlueIcon from 'img/refresh-blue.svg'
import thumbsDownIcon from 'img/thumbs-down.svg'
import thumbsDownDisabled from 'img/thumbs-down-disabled.svg'
import thumbsUpDisabled from 'img/thumbs-up-disabled.svg'
import thumbsUpIcon from 'img/thumbs-up.svg'
import trashIcon from 'img/dashicons-trash.svg'
import React, { useState, useCallback, useMemo, memo, useRef, useEffect } from 'react'
import { Question, FillBlankQuestion, MultipleChoiceQuestion, ShortAnswerQuestion } from 'types'

import { FillBlankViewer, FillBlankEditor } from './question-types/fill-blank'
import { MultipleChoiceViewer, MultipleChoiceEditor } from './question-types/multiple-choice'
import { ShortAnswerViewer, ShortAnswerEditor } from './question-types/short-answer'

import 'css/components/lesson-builder/question-slide.css'
import printStyles from 'css/components/lesson-builder/print-overlay.module.css'
import { useEditQuestionEduqueryMutation } from 'util/rtk-api'
import { useLesson } from 'contexts/lesson-context'

// Type guards for discriminated union
const isFillBlankQuestion = (question: Question): question is FillBlankQuestion => {
  return question.type === 'fill-blank'
}

const isMultipleChoiceQuestion = (question: Question): question is MultipleChoiceQuestion => {
  return question.type === 'multiple-choice'
}

const isShortAnswerQuestion = (question: Question): question is ShortAnswerQuestion => {
  return question.type === 'short-answer'
}

const QUESTION_COMPONENTS = {
	'multiple-choice': MultipleChoiceViewer,
	'short-answer': ShortAnswerViewer,
	'fill-blank': FillBlankViewer
} as const;

const QUESTION_EDITORS = {
	'fill-blank': FillBlankEditor,
	'multiple-choice': MultipleChoiceEditor,
	'short-answer': ShortAnswerEditor
} as const;

const DifficultyRow = memo(({ currentDifficulty, handleDifficultyChange }: { currentDifficulty: string, handleDifficultyChange: (difficulty: string) => void }) => {
	return (<div className="difficulty-row">
		{Object.values(ASSIGNED_DIFFICULTIES).map((difficulty: string) => (
			<div
				key={difficulty}
				className={`difficulty-tab ${currentDifficulty === difficulty ? 'active' : ''}`}
				onClick={() => handleDifficultyChange(difficulty)}
			>
				{difficulty}
			</div>
		))}
	</div>)
})

const DeleteConfirmOverlay = ({ onConfirm, onCancel }: { onConfirm: () => void, onCancel: () => void }) => {
	return (
		<div className="overlay">
			<div className="overlay-content-container delete-confirm-container">
				<div className="overlay-content-header">
					Delete Question
				</div>
				<div className="overlay-content-body">
					<p className="delete-confirm-text">
						Some of your students may be left without questions for this section if you delete this question. Please make sure this is what you want to do.
					</p>
					<p className="delete-confirm-warning">
						THIS IS PERMANENT AND CAN'T BE UNDONE. Are you sure?
					</p>
					<div className={printStyles.navRow}>
						<div />
						<div className="delete-confirm-actions">
							<button className={printStyles.secondaryBtn} onClick={onCancel}>Cancel</button>
							<button className={printStyles.destructiveBtn} onClick={onConfirm}>Delete Question</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

const QuestionContainer = memo(({ question, onApprove, onEdit, canDelete, onDelete, canEdit }: { question: Question, onApprove: (approved : boolean) => void, onEdit: (payload: Partial<Question>) => void, canDelete?: boolean, onDelete?: () => void, canEdit?: boolean }) => {
	const [isEditing, setIsEditing] = useState(false)
	const [showFeedback, setShowFeedback] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	return (
		<div className="question-container">
			<div className="question-container-top-row">
				{question.displayLabel && (
					<div className="question-display-label">{question.displayLabel}</div>
				)}
				{canDelete && onDelete && (
					<button
						className="question-delete-btn"
						onClick={() => setShowDeleteConfirm(true)}
						title="Delete question"
					>
						<img src={trashIcon} alt="Delete" />
					</button>
				)}
			</div>
			{isEditing ? (
				<>
					<QuestionEditor
						question={question}
						setIsEditing={setIsEditing}
						onEdit={onEdit}
						setShowFeedback={setShowFeedback}
					/>
				</>
			) : (
				<>
					<QuestionContent
						question={question}
						setIsEditing={setIsEditing}
						onApprove={onApprove}
						onEdit={onEdit}
						showFeedback={showFeedback}
						setShowFeedback={setShowFeedback}
						canEdit={canEdit}
					/>

				</>
			)}
			{showFeedback && (
				<FeedbackOverlay
					onEdit={onEdit}
					setShowFeedback={setShowFeedback}
					questionId={question.id}
					question={question}
				/>
			)}
			{showDeleteConfirm && (
				<DeleteConfirmOverlay
					onConfirm={() => {
						setShowDeleteConfirm(false)
						onDelete!()
					}}
					onCancel={() => setShowDeleteConfirm(false)}
				/>
			)}
		</div>
	)
})

/**
 * Renders the appropriate question component based on type
 */
const QuestionContent = memo(({ question, setIsEditing, onApprove, onEdit, showFeedback, setShowFeedback, canEdit }: { question: Question, setIsEditing: (isEditing: boolean) => void, onApprove: (approved: boolean) => void, onEdit: (payload: Partial<Question>) => void, showFeedback: boolean, setShowFeedback: (showFeedback: boolean) => void, canEdit?: boolean }) => {
	const handleRemoveImage = useCallback((optionIndex?: number) => {
		if (optionIndex !== undefined) {
			const currentUrls = (question as any).optionImageUrls || {}
			const newUrls = { ...currentUrls }
			delete newUrls[optionIndex]
			onEdit({ optionImageUrls: newUrls } as Partial<Question>)
		} else {
			onEdit({ imageUrl: undefined } as Partial<Question>)
		}
	}, [question, onEdit])

	if (!question) {
		return (
			<p className="question-text">No question available for this difficulty level.</p>
		)
	}

	const QuestionComponent = QUESTION_COMPONENTS[question.type]

	const handleApprove = (approved: boolean) => {
		onApprove(approved)
	}

	if (!QuestionComponent) {
		return (
			<>
				<p className="question-text">{question.question}</p>
				<div className="question-answer">{question.answer}</div>
			</>
		)
	}

	return (
		<div>
			<QuestionComponent question={question} onRemoveImage={canEdit !== false ? handleRemoveImage : undefined} />
			<ViewActionRow
				isApproved={question.approved}
				isDisapproved={question.isDisapproved}
				handleApprove={handleApprove}
				setIsEditing={setIsEditing}
				setShowFeedback={setShowFeedback}
				canEdit={canEdit} />
		</div>
	)
})

const ViewActionRow = memo(({ isApproved, isDisapproved, handleApprove, setIsEditing, setShowFeedback, canEdit }: { isApproved: boolean, isDisapproved: boolean, handleApprove: ( boolean ) => void, setIsEditing: (isEditing: boolean) => void, setShowFeedback: (showFeedback: boolean) => void, canEdit?: boolean }) => {
	const [showThumbsDownTooltip, setShowThumbsDownTooltip] = useState(false)
	const [showRefreshTooltip, setShowRefreshTooltip] = useState(false)
	const thumbsDownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!showThumbsDownTooltip) return
		const handleClickOutside = (e: MouseEvent) => {
			if (thumbsDownRef.current && !thumbsDownRef.current.contains(e.target as Node)) {
				setShowThumbsDownTooltip(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [showThumbsDownTooltip])

	return (
		<div className="view-actions-row">
			<div className="view-action-icon-container">
				<img
					src={canEdit === false || isApproved ? thumbsUpDisabled : thumbsUpIcon}
					alt="thumbs up"
					className="view-action-icon"
					onClick={canEdit !== false ? () => handleApprove(true) : undefined}
				/>
			</div>
			<div className="view-action-icon-container" ref={thumbsDownRef}>
				<img
					src={canEdit === false || isDisapproved ? thumbsDownDisabled : thumbsDownIcon}
					alt="thumbs down"
					className="view-action-icon"
					onClick={canEdit !== false ? () => {setShowThumbsDownTooltip(!showThumbsDownTooltip); handleApprove(false);} : undefined} />
				{showThumbsDownTooltip && <ThumbsDownTooltip />}
			</div>
			{canEdit !== false && (
				<div className="view-action-icon-container">
					<img
						src={refreshBlueIcon}
						alt="refresh"
						className="view-action-icon"
						onMouseOver={() => setShowRefreshTooltip(true)}
						onMouseOut={() => setShowRefreshTooltip(false)}
						onClick={() => setShowFeedback(true)}
					/>
					{showRefreshTooltip && <RefreshTooltip />}
				</div>
			)}
			{canEdit !== false && (
				<img
					src={editIcon}
					alt="edit"
					className="view-action-icon"
					onClick={() => setIsEditing(true)}
				/>
			)}
		</div>
	)
})

const QuestionEditor = ({ question, setShowFeedback, onEdit, setIsEditing }: { question: Question, setShowFeedback: (showFeedback: boolean) => void, onEdit: (payload: Partial<Question>) => void, setIsEditing: (isEditing: boolean) => void }) => {
    const QuestionEditorComponent: React.FC<{ setIsEditing: (isEditing: boolean) => void, onEdit: (payload: Partial<Question>) => void, question: Question, setShowFeedback?: (showFeedback: boolean) => void }> = QUESTION_EDITORS[question.type]

	// Type-safe wrapper for onEdit
	const handleEdit = (payload: Partial<Question>) => {
		onEdit(payload)
	}

	return (
		<>
			<div className="question-editor">
                <QuestionEditorComponent
                    setIsEditing={setIsEditing}
                    onEdit={handleEdit}
                    question={question}
                    setShowFeedback={setShowFeedback}
                />
			</div>
		</>
	)
}

// EditActionRow moved to components/edit-action-row

// Moved AddImageOverlay into its own reusable component

const ThumbsDownTooltip = memo(() => {
	return (
		<div className="thumbs-down-tooltip">
			Please edit or get a new question until you can approve this. The lesson will not be ready unless this is complete.
		</div>
	)
})

const RefreshTooltip = memo(() => {
	return (
		<div className="refresh-tooltip">
			Get new question
		</div>
	)
})

/**
 * Main question slide component
 * @param {Object} props
 * @param {number} [props.slideIndex] - Index of the slide
 * @param {Object} [props.slideQuestions] - Questions for each difficulty level
 * @param {string} [props.learningStage] - Learning stage of the question
 * @param {function} [props.onApproveQuestion] - Function to call when the question is approved
 * @param {function} [props.dispatch] - Function to dispatch actions to the store
 * @param {boolean} [props.canDelete] - Whether delete is available (Draft mode, no results)
 * @param {function} [props.onDeleteQuestion] - Function to call when a question is deleted
 */
export const QuestionSlide = ({ slideIndex, slideQuestions = [], learningStage = 'Pre-Assessment', onApproveQuestion, onApproveAll, dispatch, canDelete, onDeleteQuestion, canEdit }) => {
	const [currentDifficulty, setCurrentDifficulty] = useState(ASSIGNED_DIFFICULTIES.EMERGING)
	const { lessonId } = useLesson()
	const [editQuestionEduquery] = useEditQuestionEduqueryMutation()

	const handleDifficultyChange = useCallback((difficulty) => {
		setCurrentDifficulty(difficulty)
	}, [])

	// Memoize current question to prevent unnecessary recalculations
	const currentDifficultyQuestions = useMemo(() =>
		slideQuestions.filter(difficultyQuestions => difficultyQuestions.difficulty === currentDifficulty)[0], [slideQuestions, currentDifficulty]
	)

    const handleApprove = useCallback((questionIndex, difficulty, approved) => {
        onApproveQuestion(questionIndex, difficulty, approved)
    }, [onApproveQuestion])

	const isAssessmentSlide = learningStage === 'Pre-Assessment' || learningStage === 'Post-Assessment';

	const handleEdit = useCallback((questionIndex: number, difficulty: string, payload: Partial<Question>) => {
		console.log('🎯 handleEdit called:', { questionIndex, difficulty, payload })
		// Look up question using passed difficulty parameter instead of memoized currentDifficultyQuestions
		// This makes the callback more stable (fewer dependency changes)
		const difficultyGroup = slideQuestions.find(dq => dq.difficulty === difficulty)
		const originalQuestion: Question | undefined = difficultyGroup?.questions?.[questionIndex]
		if (!originalQuestion) {
			console.log('❌ No original question found')
			return
		}

		console.log('📋 Original question:', originalQuestion)

		// Only proceed if payload introduces a real change
		const didChange = Object.entries(payload).some(([key, value]) => {
			const prev = (originalQuestion as any)[key]
			const changed = typeof value === 'object' && value !== null
				? JSON.stringify(prev) !== JSON.stringify(value)
				: prev !== value
			console.log(`🔍 Checking ${key}:`, { prev, value, changed })
			return changed
		})
		console.log('📊 Did change?', didChange)
		if (!didChange) {
			console.log('⚠️ No changes detected - skipping dispatch')
			return
		}
		console.log('✅ Changes detected - dispatching EDIT_QUESTION')

		// Update local state - always use EDIT_QUESTION with slideIndex
		dispatch({
			type: 'EDIT_QUESTION',
			difficulty: difficulty,
			slideIndex: slideIndex,
			questionIndex: questionIndex,
			payload: payload
		});

		// Call backend to persist edit to EduQuery
		// Use payload directly (not merged) to ensure new data from refinement is saved
		// The merged object uses originalQuestion which was captured before state update
		if (lessonId && originalQuestion.id) {
			try {
				// For assessment questions, sync both Pre and Post to database
				if (isAssessmentSlide) {
					// Extract base ID (remove _Pre-Assessment or _Post-Assessment suffix)
					const baseId = originalQuestion.id.replace(/_(Pre-Assessment|Post-Assessment)$/, '')

					// Sync both Pre and Post assessment versions
					const preId = `${baseId}_Pre-Assessment`
					const postId = `${baseId}_Post-Assessment`

					editQuestionEduquery({ lessonId, questionId: preId, question: { ...payload, id: preId } as Question })
					editQuestionEduquery({ lessonId, questionId: postId, question: { ...payload, id: postId } as Question })
				} else {
					// For non-assessment questions, just sync the single question
					editQuestionEduquery({ lessonId, questionId: originalQuestion.id, question: { ...originalQuestion, ...payload } as Question })
				}
			} catch {}
		}
	}, [dispatch, slideIndex, isAssessmentSlide, slideQuestions, lessonId, editQuestionEduquery]);

	const handleDelete = useCallback((questionIndex: number, difficulty: string, questionId: string) => {
		if (onDeleteQuestion) {
			onDeleteQuestion(questionIndex, difficulty, questionId)
		}
	}, [onDeleteQuestion])

	const allSectionApproved = slideQuestions.every(diffGroup =>
		diffGroup.questions.every(q => q.approved)
	)

	return (
		<div className="question-slide">
			<div className="question-slide-header">
				<h1 className="question-type">{learningStage}</h1>
				<button
					className={`approve-all-btn${allSectionApproved ? ' approved' : ''}`}
					onClick={onApproveAll}
					disabled={allSectionApproved}
				>
					Approve All
				</button>
			</div>

			<div className={`editing-warning ${learningStage === "Post-Assessment" || learningStage === "Pre-Assessment" ? "" : "display-none"}`}>
				NOTE: SAME QUESTIONS!
				You are about to edit both pre-assessment and post-assessment questions.
			</div>

			<>
				<DifficultyRow
					currentDifficulty={currentDifficulty}
					handleDifficultyChange={handleDifficultyChange} />
				<div className="question-list-container">
					{currentDifficultyQuestions?.questions.map((question, index) => (
						<QuestionContainer
							key={question.id}
							question={question}
                            onApprove={(approved) => handleApprove(index, currentDifficulty, approved)}
							onEdit={(payload) => handleEdit(index, currentDifficulty, payload)}
							canDelete={canDelete}
							onDelete={() => handleDelete(index, currentDifficulty, question.id)}
							canEdit={canEdit}
						/>
					))}
				</div>
			</>
		</div >
	)
}
